import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Shield, Clock, Award, Droplets } from 'lucide-react';
import { Button } from './ui/button';

// Hero image URL - preloaded in index.html
const HERO_IMAGE_URL = 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1920&q=80';

const Hero = () => {
  const { language, t } = useLanguage();

  const features = [
    { icon: Shield, text: t.about.features[0].title },
    { icon: Clock, text: t.about.features[1].title },
    { icon: Award, text: t.about.features[3].title },
  ];

  return (
    <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background with optimized image loading */}
      <div className="absolute inset-0">
        {/* Use img tag with fetchpriority for LCP optimization */}
        <img
          src={HERO_IMAGE_URL}
          alt=""
          width={1920}
          height={1080}
          fetchpriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'grayscale(30%)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-purple-800/85 to-purple-900/75 md:from-purple-900/90 md:via-purple-800/70 md:to-purple-900/60" />
      </div>

      {/* Decorative Elements - hidden on mobile */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-orange-400/20 rounded-full blur-3xl hidden md:block" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl hidden md:block" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-20 w-full">
        <div className="max-w-2xl">
          {/* Badge - smaller on mobile */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm mb-4 md:mb-6 border border-white/20">
            <Droplets size={14} className="text-orange-400" />
            <span className="hidden sm:inline">
              {language === 'sv' ? 'En professionell städning med den personliga touchen' : 'Professional cleaning with a personal touch'}
            </span>
            <span className="sm:hidden">
              {language === 'sv' ? 'Professionell städservice' : 'Professional cleaning'}
            </span>
          </div>

          {/* Tagline */}
          <p className="text-orange-400 text-sm md:text-lg font-medium mb-2 md:mb-4 tracking-wide">
            {language === 'sv' ? '"RENT, SNYGGT OCH BILLIGT"' : '"CLEAN, BEAUTIFUL AND AFFORDABLE"'}
          </p>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
            {language === 'sv' ? (
              <>Välkommen till <span className="text-orange-400">Stockholms Elite</span>Städning</>
            ) : (
              <>Welcome to <span className="text-orange-400">Stockholm's Elite</span> Cleaning</>
            )}
          </h1>

          {/* Subtitle */}
          <p className="text-base md:text-xl text-purple-100 mb-6 md:mb-8 leading-relaxed">
            {language === 'sv' 
              ? 'Vi skapar rena och fräscha miljöer för hem, arbetsplatser och fastigheter.'
              : 'We create clean and fresh environments for homes, workplaces and properties.'
            }
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-12">
            <a href="#calculator" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-semibold shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all hover:scale-105">
                {t.hero.cta}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </a>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-semibold backdrop-blur-sm">
                {t.hero.secondaryCta}
              </Button>
            </Link>
          </div>

          {/* Feature Pills - horizontal scroll on mobile */}
          <div className="flex flex-wrap gap-2 md:gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-1.5 md:gap-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full text-white text-xs md:text-sm border border-white/10"
              >
                <feature.icon size={14} className="text-orange-400 flex-shrink-0" />
                <span className="whitespace-nowrap">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator - hidden on mobile */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
